import {
  HEALTH_LABEL,
  HEALTH_STATUSES,
  MARKETS,
  MONETIZATION_LABEL,
  MONETIZATION_STATUSES,
  type CreateChannelInput,
  type HealthStatus,
  type MonetizationStatus,
} from '@qlk/shared';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { EmptyState, MAU_DU_LIEU_MAC_DINH, Nut, OChon, PageHeader } from '../../../ui/index.js';
import { useCapNhatKenh, useKenh, useNhomKenh, useTaoKenh } from '../api.js';

const O =
  'vung-bam w-full rounded-[10px] border border-vien bg-be-mat px-3 py-2 text-co-ban text-chu placeholder:text-chu-mo';

const MAU_MAC_DINH = MAU_DU_LIEU_MAC_DINH;

type Form = CreateChannelInput;

const TRONG: Form = {
  name: '',
  handle: '',
  niche: '',
  market: 'EN',
  language: 'Tiếng Anh',
  avatarColor: MAU_MAC_DINH,
  monetizationStatus: 'not_eligible',
  healthStatus: 'ok',
};

/** Dùng chung cho "Thêm kênh" (`/kenh/moi`) và "Sửa kênh" (`/kenh/:id/sua`). */
export function TrangFormKenh() {
  const { id } = useParams();
  const dieuHuong = useNavigate();
  const laSua = Boolean(id);

  const kenh = useKenh(id);
  const nhom = useNhomKenh();
  const tao = useTaoKenh();
  const capNhat = useCapNhatKenh(id ?? '');

  const [form, setForm] = useState<Form>(TRONG);

  useEffect(() => {
    if (!kenh.data) return;
    setForm({
      name: kenh.data.name,
      handle: kenh.data.handle,
      niche: kenh.data.niche,
      market: kenh.data.market,
      language: kenh.data.language,
      groupId: kenh.data.groupId,
      youtubeChannelId: kenh.data.youtubeChannelId,
      avatarColor: kenh.data.avatarColor,
      monetizationStatus: kenh.data.monetizationStatus,
      healthStatus: kenh.data.healthStatus,
      healthNote: kenh.data.healthNote,
    });
  }, [kenh.data]);

  const dangChay = tao.isPending || capNhat.isPending;
  const loi = tao.error ?? capNhat.error;

  if (laSua && kenh.error) {
    return (
      <div className="rounded-the border border-vien bg-be-mat">
        <EmptyState loi title="Không mở được kênh" description={kenh.error.message} />
      </div>
    );
  }

  function luu() {
    const du: Form = {
      ...form,
      groupId: form.groupId ? form.groupId : null,
      youtubeChannelId: form.youtubeChannelId ? form.youtubeChannelId : null,
      healthNote: form.healthNote ? form.healthNote : null,
    };

    if (laSua && id) {
      capNhat.mutate(du, {
        onSuccess: () => {
          void dieuHuong(`/kenh/${id}`);
        },
      });
      return;
    }

    tao.mutate(du, {
      onSuccess: (kenhMoi) => {
        void dieuHuong(`/kenh/${kenhMoi.id}`);
      },
    });
  }

  return (
    <>
      <PageHeader
        breadcrumb={['VẬN HÀNH', 'Quản lý kênh']}
        title={laSua ? 'Sửa kênh' : 'Thêm kênh'}
        description="Thông tin hồ sơ cơ bản. Số liệu và doanh thu lấy tự động từ YouTube."
        actions={
          <Link
            to={laSua && id ? `/kenh/${id}` : '/kenh'}
            className="text-co-ban font-semibold text-chu-phu hover:text-chu"
          >
            ← Quay lại
          </Link>
        }
      />

      <form
        className="flex max-w-3xl flex-col gap-4 rounded-the border border-vien bg-be-mat p-6"
        onSubmit={(e) => {
          e.preventDefault();
          luu();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-phu font-medium text-chu-phu">Tên kênh</span>
          <input
            className={O}
            required
            value={form.name}
            onChange={(e) => {
              setForm((f) => ({ ...f, name: e.target.value }));
            }}
          />
        </label>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-phu font-medium text-chu-phu">Handle</span>
            <input
              className={O}
              required
              placeholder="@tenkenh"
              value={form.handle}
              onChange={(e) => {
                setForm((f) => ({ ...f, handle: e.target.value }));
              }}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-phu font-medium text-chu-phu">Chủ đề</span>
            <input
              className={O}
              required
              placeholder="Nhạc thư giãn"
              value={form.niche}
              onChange={(e) => {
                setForm((f) => ({ ...f, niche: e.target.value }));
              }}
            />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <OChon
            hienNhan
            label="Thị trường"
            value={form.market}
            onChange={(v) => {
              setForm((f) => ({ ...f, market: v }));
            }}
            options={MARKETS.map((m) => ({ value: m, label: m }))}
          />

          <label className="flex flex-col gap-1">
            <span className="text-phu font-medium text-chu-phu">Ngôn ngữ</span>
            <input
              className={O}
              required
              value={form.language}
              onChange={(e) => {
                setForm((f) => ({ ...f, language: e.target.value }));
              }}
            />
          </label>

          {/*
            Nhãn là "Nhóm chủ đề", không phải "Chủ đề": ô chữ phía trên cũng tên
            "Chủ đề" nên hai ô cùng một nhãn — người dùng không biết ô nào là
            cái gì, và trình đọc màn hình đọc ra hai lần cùng một tên.
          */}
          <OChon
            hienNhan
            label="Nhóm chủ đề"
            value={form.groupId ?? ''}
            onChange={(v) => {
              setForm((f) => ({ ...f, groupId: v || null }));
            }}
            options={[
              { value: '', label: 'Không thuộc chủ đề nào' },
              ...(nhom.data ?? []).map((n) => ({ value: n.id, label: n.name })),
            ]}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <OChon
            hienNhan
            label="Trạng thái kiếm tiền"
            value={form.monetizationStatus}
            onChange={(v) => {
              setForm((f) => ({ ...f, monetizationStatus: v as MonetizationStatus }));
            }}
            options={MONETIZATION_STATUSES.map((m) => ({ value: m, label: MONETIZATION_LABEL[m] }))}
          />

          <OChon
            hienNhan
            label="Sức khỏe kênh"
            value={form.healthStatus}
            onChange={(v) => {
              setForm((f) => ({ ...f, healthStatus: v as HealthStatus }));
            }}
            options={HEALTH_STATUSES.map((h) => ({ value: h, label: HEALTH_LABEL[h] }))}
          />
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-phu font-medium text-chu-phu">Ghi chú sức khỏe</span>
          <input
            className={O}
            value={form.healthNote ?? ''}
            onChange={(e) => {
              setForm((f) => ({ ...f, healthNote: e.target.value }));
            }}
          />
        </label>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-phu font-medium text-chu-phu">Id kênh YouTube (nếu đã biết)</span>
            <input
              className={O}
              value={form.youtubeChannelId ?? ''}
              placeholder="UC..."
              onChange={(e) => {
                setForm((f) => ({ ...f, youtubeChannelId: e.target.value }));
              }}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-phu font-medium text-chu-phu">Màu avatar</span>
            <input
              type="color"
              className="vung-bam w-24 rounded-[10px] border border-vien bg-be-mat px-2"
              value={form.avatarColor ?? MAU_MAC_DINH}
              onChange={(e) => {
                setForm((f) => ({ ...f, avatarColor: e.target.value }));
              }}
            />
          </label>
        </div>

        {loi && (
          <p role="alert" className="rounded-[10px] bg-bad-nen px-3 py-2 text-co-ban text-bad-chu">
            {loi.message}
          </p>
        )}

        <div className="flex items-center gap-3">
          <Nut kieu="chinh" type="submit" dangChay={dangChay}>
            {laSua ? 'Lưu thay đổi' : 'Thêm kênh'}
          </Nut>
          <Link
            to={laSua && id ? `/kenh/${id}` : '/kenh'}
            className="vung-bam inline-flex items-center rounded-[10px] px-4 text-co-ban font-semibold text-chu-phu hover:bg-neutral-nen"
          >
            Huỷ
          </Link>
        </div>
      </form>
    </>
  );
}
